import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faStop,
  faPause,
  faUpload,
  faLeftRight,
} from "@fortawesome/free-solid-svg-icons";
import "./AudioRecorder.css";
import "./scorebox.css";
import AddAudioModal from "./AddAudioModal";
import InfoTooltip from "../../../InfoTooltip";
import TipButton from "../../../TipButton";
import StudentInfoTooltip from "../../../StudentInfoTooltip";
import { handleScoreChange } from "./helpers/scoreHelpers";

const AudioRecorder = ({
  onSave,
  onRecordingComplete,
  question,
  onChange,
  isStudentView,
  border,
  score: initialScore = 0,
  tip,
  questionNumber,
  onFlip,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState("");
  const [error, setError] = useState("");
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const streamRef = useRef(null);

  const [showAddAudioModal, setShowAddAudioModal] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(question || "");
  const [score, setScore] = useState(initialScore);

  useEffect(() => {
    return () => {
      stopRecording();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        streamRef.current = null;
      }
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    setScore(initialScore);
  }, [initialScore]);

  useEffect(() => {
    setCurrentQuestion(question || "");
  }, [question]);

  const startRecording = async () => {
    try {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        setIsRecording(true);
        timerIntervalRef.current = setInterval(() => {
          setRecordingDuration((prev) => prev + 1);
        }, 1000);
        drawWaveform();
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: 22050,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
        streamRef.current = stream;
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)({ sampleRate: 22050 });
        analyserRef.current = audioContextRef.current.createAnalyser();
        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);

        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: "audio/webm;codecs=opus",
          audioBitsPerSecond: 32000,
        });
        mediaRecorderRef.current.ondataavailable = (event) => {
          audioChunksRef.current.push(event.data);
        };

        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, {
            type: "audio/webm",
          });
          const audioURL = URL.createObjectURL(audioBlob);
          setAudioURL(audioURL);
          await onSave(audioBlob);
          setIsProcessing(false);
          onRecordingComplete();
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
        setRecordingDuration(0);
        timerIntervalRef.current = setInterval(() => {
          setRecordingDuration((prev) => prev + 1);
        }, 1000);

        drawWaveform();
      }
    } catch (err) {
      setError(`Failed to start recording: ${err.message}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && (isRecording || isPaused)) {
      setIsProcessing(true);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      clearInterval(timerIntervalRef.current);
      cancelAnimationFrame(animationFrameRef.current);

      // Close the audio stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        streamRef.current = null;
      }

      // Close the audio context
      if (audioContextRef.current) {
        audioContextRef.current.close().then(() => {
          audioContextRef.current = null;
        });
      }

      // Reset other resources
      analyserRef.current = null;
      audioChunksRef.current = [];

      // We'll call onRecordingComplete after the audio is saved
      onRecordingComplete();
    }
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    canvasCtx.clearRect(0, 0, width, height);

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyserRef.current.getByteTimeDomainData(dataArray);

      canvasCtx.fillStyle = "rgb(245, 245, 245)";
      canvasCtx.fillRect(0, 0, width, height);
      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = "rgb(0, 0, 0)";
      canvasCtx.beginPath();

      const sliceWidth = (width * 1.0) / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * height) / 2;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(width, height / 2);
      canvasCtx.stroke();
    };

    draw();
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      setIsRecording(false);
      clearInterval(timerIntervalRef.current);
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const handleQuestionChange = (e) => {
    const newQuestion = e.target.value;
    setCurrentQuestion(newQuestion);
    onChange({ question: newQuestion });
  };

  const onScoreChange = handleScoreChange(onChange);

  return (
    <div className={`question-container ${border}`}>
      <div className="question-header">
        {questionNumber && (
          <span className="question-number">{questionNumber}.</span>
        )}
        {isStudentView ? (
          <>
            {currentQuestion ? (
              <div className="audio-recorder-question">{currentQuestion}</div>
            ) : (
              <div className="audio-recorder-question">
                You can record your voice for this question.
              </div>
            )}
            <div className="header-spacer"></div>
            <StudentInfoTooltip type="Audio Recorder" />
          </>
        ) : (
          <>
            <input
              type="text"
              value={currentQuestion}
              onChange={handleQuestionChange}
              placeholder="Describe what your students should record here (optional)"
              className="question-input"
            />
            <InfoTooltip type="Audio Recorder" />
          </>
        )}
      </div>
      <div className="recorder">
        {!isRecording && !isPaused ? (
          <>
            <button
              onClick={startRecording}
              className="icon-button record-button"
            >
              <FontAwesomeIcon icon={faMicrophone} size="lg" />
            </button>
            <button
              onClick={() => setShowAddAudioModal(true)}
              className="icon-button upload-button"
            >
              <FontAwesomeIcon icon={faUpload} size="lg" />
            </button>
          </>
        ) : (
          <>
            <button onClick={stopRecording} className="icon-button stop-button">
              <FontAwesomeIcon icon={faStop} size="lg" />
            </button>
            <button
              onClick={isPaused ? startRecording : pauseRecording}
              className="icon-button record-button"
            >
              <FontAwesomeIcon
                icon={isPaused ? faMicrophone : faPause}
                size="lg"
              />
            </button>
          </>
        )}

        <div
          className={`waveform-text ${
            isRecording
              ? "recording"
              : isPaused
              ? "paused"
              : isProcessing
              ? "processing"
              : "idle"
          }`}
        >
          {isRecording
            ? "Recording in progress..."
            : isPaused
            ? "Press the microphone button again to continue recording..."
            : isProcessing
            ? "Processing... Please wait."
            : "Press the microphone button on the left to start recording..."}
        </div>
        <canvas
          ref={canvasRef}
          className="waveform-canvas"
          width="300"
          height="100"
        />
      </div>

      <div className="recorder-controls">
        <div className="button-wrapper"></div>
        <TipButton
          tip={tip}
          onChange={onChange}
          isStudentView={isStudentView}
          onFlip={onFlip}
        />
        <div className="right-controls">
          <div className="time-display">
            {formatAudioTime(recordingDuration)} / {formatAudioTime(300)}
          </div>
          <div className="scorebox-container">
            <label htmlFor="score">Score:</label>
            <input
              type="number"
              id="score"
              value={score}
              onChange={onScoreChange}
              min="0"
              step="1"
              readOnly={isStudentView}
              className={isStudentView ? "student-view" : ""}
            />
          </div>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}
      <AddAudioModal
        show={showAddAudioModal}
        onClose={() => setShowAddAudioModal(false)}
        onSave={(audioData) => {
          onSave(audioData.audio);
          setShowAddAudioModal(false);
        }}
      />
    </div>
  );
};

const formatAudioTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

export default AudioRecorder;
